import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import { FiEdit2, FiLock, FiCheck, FiX } from 'react-icons/fi';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { formatISO } from 'date-fns';
import { formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import moment from 'moment';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for unique tooltip IDs

const EditableField = ({
  label,
  name,
  value,
  onChange,
  isEditable = true,
  isLoading = false,
  type = 'text',
  options = [],
  maxLength,
  rows = 4,
  unit,
  isMulti = false,
  allowFutureDates = true,
  onEditClick,
  onInputChange,
  onMenuOpen,
  selectLoading = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const wasLoading = useRef(false);
  const datePickerRef = useRef(null);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const tooltipId = useRef(`tooltip-${uuidv4()}`).current; // Unique tooltip ID

  useEffect(() => {
    if (wasLoading.current && !isLoading) {
      setIsEditing(false);
    }
    wasLoading.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setIsDateOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEditClick = () => {
    if (isEditable) {
      setTempValue(value);
      setIsEditing(true);
      if (type === 'datetime-local') setIsDateOpen(true);
    }
  };

  const handleSave = () => {
    onChange(tempValue, name);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
    setIsDateOpen(false);
  };

  const getSelectedOptions = () => {
    if (isMulti) {
      if (!tempValue) return [];
      if (!Array.isArray(tempValue)) return [];
      return tempValue.map(val => options.find(opt => opt.value === val));
    }
    return options.find(opt => opt.value === tempValue) || null;
  };

  const isNumberField = type === 'number';

  const renderMultiValueDisplay = (values) => {
    if (!values) return null;
    if (!Array.isArray(values)) return <span>{values}</span>;
    if (values.length === 0) return null;

    const excerpt = values
      .map(val => options.find(opt => opt.value === val)?.label || val)
      .join(', ');
    const isTruncated = excerpt.length > 70;
    const truncatedExcerpt = excerpt.length > 70 ? `${excerpt.substring(0, 70)}...` : excerpt;

    return (
      <div className="flex flex-wrap gap-1 w-full">
        <span
          className="truncate"
          {...(isTruncated && {
            'data-tooltip-id': tooltipId,
            'data-tooltip-content': excerpt,
            'data-tooltip-place': 'top'
          })}
        >
          {truncatedExcerpt}
        </span>
        {isTruncated && (
          <Tooltip
            id={tooltipId}
            place="top"
            className="z-50"
            style={{
              maxWidth: '250px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              padding: '8px',
              fontSize: '0.875rem',
              backgroundColor: '#000d',
              color: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            }}
          />
        )}
      </div>
    );
  };

  const validDates = (current) => {
    if (allowFutureDates) return true;
    return current.isBefore(new Date());
  };

  const renderDisplayValue = () => {
    let displayValue;
    if (type === 'datetime-local') {
      displayValue = formatUTCTimestampToLocal(value, 'yyyy-MM-dd hh:mm a');
    } else if (type === 'select' && isMulti) {
      return renderMultiValueDisplay(value);
    } else if (type === 'select') {
      displayValue = options?.find((opt) => opt.value === value)?.label || value;
    } else {
      displayValue = (value === 0 || value) ? value : '';
    }

    const isTruncated = typeof displayValue === 'string' && displayValue.length > 48;
    const excerpt = typeof displayValue === 'string' && displayValue.length > 48
      ? `${displayValue.substring(0, 48)}...`
      : displayValue;

    return (
      <>
        <span
          className="truncate"
          {...(isTruncated && {
            'data-tooltip-id': tooltipId,
            'data-tooltip-content': displayValue,
            'data-tooltip-place': 'top'
          })}
        >
          {excerpt}
        </span>
        {isTruncated && (
          <Tooltip
            id={tooltipId}
            place="top"
            className="z-50"
            style={{
              maxWidth: '250px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              padding: '8px',
              fontSize: '0.875rem',
              backgroundColor: '#000d',
              color: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            }}
          />
        )}
      </>
    );
  };

  return (
    <div className="mb-1 flex flex-col items-start w-full justify-between">
      <p className="gap-0.5 text-sm flex items-center font-medium text-gray-700 mb-0.5">
        {label}
        {unit && (
          <span className="text-gray-500 text-xs mt-0.5">({unit})</span>
        )}
      </p>

      <div
        className="relative w-full -ml-1"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isEditing ? (
          <div className="relative flex items-center gap-1 w-full">
            {type === 'select' ? (
              <div className="xs:w-[90%] w-full relative">
                <Select
                  value={getSelectedOptions()}
                  onChange={(selected) => {
                    if (isMulti) {
                      setTempValue(selected ? selected.map(opt => opt.value) : []);
                    } else {
                      setTempValue(selected?.value);
                    }
                  }}
                  onInputChange={onInputChange}
                  onMenuOpen={onMenuOpen}
                  isLoading={selectLoading}
                  options={options}
                  isSearchable
                  isMulti={isMulti}
                  classNamePrefix="custom-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '29px',
                      borderRadius: 8,
                      borderColor: '#60A5FA',
                      boxShadow: 'none',
                      fontSize: '14px',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 8px',
                      minHeight: '5px',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      lineHeight: '2',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#DBEAFE',
                      borderRadius: '9999px',
                      display: 'flex',
                      alignItems: 'center',
                      margin: '2px',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#1E40AF',
                      fontSize: '12px',
                      padding: '2px 6px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#93C5FD',
                      ':hover': {
                        backgroundColor: 'transparent',
                        color: '#1E40AF',
                      },
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: '28px',
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: 4,
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      padding: 4,
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      padding: '6px 12px',
                      fontSize: '13px',
                      minHeight: '28px',
                      lineHeight: '1.2',
                      backgroundColor: isSelected
                        ? '#DBEAFE'
                        : isFocused
                          ? '#EFF6FF'
                          : '#fff',
                      color: '#111827',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }),
                    menuList: (base) => ({
                      ...base,
                      paddingTop: 4,
                      paddingBottom: 4,
                    }),
                  }}
                  maxMenuHeight={160}
                />
              </div>
            ) : type === 'textarea' ? (
              <textarea
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                className="border border-blue-400 bg-white text-sm rounded-lg outline-none xs:w-[75%] w-full px-2 py-[3px] focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all"
                autoFocus
                // maxLength={100}
                rows={rows}
              />
            ) : type === 'datetime-local' ? (
              <div className="relative xs:w-[75%] w-full" ref={datePickerRef}>
                <Datetime
                  value={
                    tempValue && moment(tempValue, moment.ISO_8601, true).isValid()
                      ? moment(tempValue)
                      : null
                  }
                  onChange={(inputValue) => {
                    let dateObj;

                    if (typeof inputValue === "string") {
                      const parsed = moment(inputValue, "YYYY-MM-DD HH:mm", true);
                      if (!parsed.isValid()) return;
                      dateObj = parsed;
                    } else if (moment.isMoment(inputValue)) {
                      dateObj = inputValue;
                    }

                    if (dateObj && dateObj.isValid()) {
                      const utcFormatted = formatISO(dateObj.toDate(), { representation: 'complete' });
                      // gives 2025-09-10T08:15:00Z
                      setTempValue(utcFormatted);
                    }
                  }}
                  inputProps={{
                    className:
                      'w-full pr-10 pl-3 py-[3px] text-sm border border-blue-400 bg-white rounded-lg focus:outline-none cursor-pointer',
                    onClick: () => setIsDateOpen(true),
                  }}
                  onFocus={() => setIsDateOpen(true)}
                  open={isDateOpen}
                  dateFormat="YYYY-MM-DD"
                  timeFormat="hh:mm A"
                  utc={false}
                  isValidDate={validDates}
                  closeOnSelect={false}
                  timeConstraints={{
                    minutes: {
                      step: 1
                    }
                  }}
                />
                <FaRegCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            ) : (
              <div className='relative xs:w-[75%] w-full'>
                <input
                  type={type}
                  value={tempValue}
                  maxLength={maxLength}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isNumberField && !/^\d*\.?\d{0,2}$/.test(val)) return;
                    setTempValue(val);
                  }}
                  className="border border-blue-400 bg-white text-sm rounded-lg outline-none w-full px-2 py-[3px] focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all"
                  step={isNumberField ? '0.01' : undefined}
                  autoFocus
                />
              </div>
            )}
            <div className="flex gap-0.5 ml-0.5">
              <button
                onClick={handleSave}
                className={`p-0.5 ${isLoading ? 'text-gray-500' : 'text-green-500 hover:bg-green-50'} rounded-full transition-colors`}
                title="Save"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FiCheck size={16} />
                )}
              </button>
              <button
                onClick={handleCancel}
                className="p-0.5 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                title="Cancel"
                disabled={isLoading}
              >
                <FiX size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`flex font-semibold relative items-start justify-between text-sm px-2 py-[3px] transition-all duration-200 pl-2 min-h-7 border-l border-l-gray-300 ${isHovered
              ? 'border border-gray-300 rounded-lg'
              : 'border border-transparent rounded-none'
              }`}
            onClick={onEditClick || handleEditClick}
          >
            <div
              className={`text-gray-800 overflow-hidden w-[95%] text-sm ${type === 'textarea'
                ? 'whitespace-pre-wrap break-words max-h-[200px] '
                : 'truncate'
                }`}
            >
              {renderDisplayValue()}
            </div>

            {(isHovered || !isEditable) && (
              <div className="ml-2 mt-1 text-gray-400 flex-shrink-0">
                {isEditable ? (
                  <FiEdit2 className="hover:text-gray-600 transition-colors" />
                ) : (
                  <FiLock />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableField;